import "./styles/knightsMain.scss";
import {
  Routes,
  Route,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";
import { Suspense, lazy, useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { fetchCall, url } from "./constants/constant";
import { setPosFeatures, setProductFields, setCoinList, editMainCurrency } from "./redux/actions";
import { Layout, Drawer, message } from "antd";
import ReactTooltip from "react-tooltip";
import KnightsLoading from "./components/commonComponents/KnightsLoading";
import MobileMenu from "./components/MobileMenu";

const Login = lazy(() => import("./components/Login"));
const KnightsEmployees = lazy(() => import("./components/admin/employee/knightsEmployees"));
const KnightsEmployeePosition = lazy(() => import("./components/admin/settings/employeePosition/knightsEmployeePosition"));
const KnightsPermission = lazy(() => import("./components/admin/settings/permissions/KnightsPermission"));
const KnightsPermissionCategory = lazy(() => import("./components/admin/settings/permissionCategory/KnightsPermissionCategory"));
const KnightsRole = lazy(() => import("./components/admin/settings/roles/knightsRole"));
const KnightsCustomer = lazy(() => import("./components/customer/KnightsCustomer"));
const KnightsCustomerDebt = lazy(() => import("./components/customer/KnightsCustomerDebt"));
const KnightsSupplier = lazy(() => import("./components/supplier/KnightsSupplier"));
const KnightsSupplierReviewInvoice = lazy(() => import("./components/supplier/KnightsSupplierReviewInvoice"));
const KnightsSupplierInvoice = lazy(() => import("./components/supplier/KnightsSupplierInvoice"));
const KnightsSupplierDebt = lazy(() => import("./components/supplier/KnightsSupplierDebt"));
const KnightsService = lazy(() => import("./components/services/KnightsService"));
const KnightsAppSetting = lazy(() => import("./components/settings/KnightsAppSetting"));
const KnightsNewInvoice = lazy(() => import("./components/invoice/knightsNewInvoice"));
const KnightsReviewInvoice = lazy(() => import("./components/invoice/KnightsReviewInvoice"));
const KnghitsCategory = lazy(() => import("./components/category/knghitsCategory"));
const Products = lazy(() => import("./components/products/knightsProducts"));
const KnightsDefaultInventory = lazy(() => import("./components/inventory/KnightsDefaultInventory"));
const KnightsDefaultInventoryEmp = lazy(() => import("./components/inventory/KnightsDefaultInventoryEmp"));
const KnightsDefaultInventoryProduct = lazy(() => import("./components/inventory/KnightsDefaultInventoryProduct"));
const KnightsPrintBarcode = lazy(() => import("./components/barcode/KnightsPrintBarcode"));
const BarcodeDesigner = lazy(() => import("./components/barcode/BarcodeDesigner"));
const KnightsCharges = lazy(() => import("./components/charges/KnightsCharges"));
const KnightsAdminSetting = lazy(() => import("./components/admin/settings/appSettings/KnightsAdminSetting"));
const InvoiceTemplateSettings = lazy(() => import("./components/admin/settings/invoiceTemplate/InvoiceTemplateSettings"));
const KnightsReviewFund = lazy(() => import("./components/invoice/KnightsReviewFund"));
const Header = lazy(() => import("./components/Header"));
const ReturnInvoiceProduct = lazy(() => import("./components/invoice/RetrunInvoiceProduct"));
const KnightsLicense = lazy(() => import("./components/license/KnightsLicense"));
const KnightsPrintSetting = lazy(() => import("./components/printSetting/KnightsPrintSetting"));
const KnightsOnlineOrder = lazy(() => import("./components/invoice/onlineOrder/KnightsOnlineOrder"));
const KnightsDepartments = lazy(() => import("./components/admin/departments/knightsDepartments"));
const KnightsDepartmentOrder = lazy(() => import("./components/admin/departments/orders/KnightsDepartmentOrders"));
const KnightsBox = lazy(() => import("./components/admin/box/KnightsBox"));
const KnightsVaultTransfer = lazy(() => import("./components/admin/box/KnightsVaultTransfer"));
const KnightsVaultTransferReview = lazy(() => import("./components/admin/box/KnightsVaultTransferReview"));
const KnightsOpenFundReview = lazy(() => import("./components/fund/openFund/KnightsOpenFundReview"));
const KnightsViewDepartmentsOrders = lazy(() => import("./components/admin/departments/KnightsViewDepartmentsOrders"));
const KnightsDepartmentDebt = lazy(() => import("./components/admin/departments/KnightsDepartmentDebt"));
const KnightsInventoryProduct = lazy(() => import("./components/inventory/inventoryProduct/KnightsInventoryProduct"));
const KnightsInventoryStat = lazy(() => import("./components/inventory/KnightsInventoryStat"));
const KnightsWhatsApp = lazy(() => import("./components/whatsapp/KnightsWhatsApp"));
const KnightsWhatsAppQueue = lazy(() => import("./components/whatsapp/KnightsWhatsAppQueue"));
const DebtReminderSettings = lazy(() => import("./components/whatsapp/DebtReminderSettings"));
const AuditLogs = lazy(() => import("./components/audit/AuditLogs"));
const WebsiteOrders = lazy(() => import("./components/admin/orders/WebsiteOrders"));
const IntegratedProducts = lazy(() => import("./components/products/IntegratedProducts"));
const ReportsPage = lazy(() => import("./components/reports/ReportsPage"));

message.config({
  zIndex: 100000000,
  maxCount: 1,
});

function App() {
  const dispatch = useDispatch();
  const currLocation = useLocation();
  const navigate = useNavigate();
  const [dateDiffer, setDateDiffer] = useState(-1);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const toggleMobileMenu = useCallback(() => setMobileMenuOpen((p) => !p), []);
  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);
  useEffect(() => {
    fetchCall(
      url.checkAppLicense,
      (data) => {
        if (data.status === 406) {
          // Try fetching the key from the license portal automatically before
          // falling back to the manual upload page.
          fetchCall(
            url.downloadAppLicense,
            () => {
              setDateDiffer(200);
            },
            () => {
              message.error(data.message);
              navigate("/license");
            },
            "GET"
          );
          return;
        }
        setDateDiffer(data.differ);
      },
      (data) => {
        console.log(data);
      },
      "GET"
    );
  }, []);
  useEffect(() => {
    fetchCall(
      url.getPublicSettings,
      (data) => {
        if (data.status === 200 && data.items) {
          if (data.items.pos_features) {
            dispatch(setPosFeatures(data.items.pos_features));
          }
          if (data.items.product_fields) {
            dispatch(setProductFields(data.items.product_fields));
          }
          if (data.items.coins) {
            dispatch(setCoinList(data.items.coins));
          }
          if (data.items.main_currency) {
            dispatch(editMainCurrency(data.items.main_currency));
          }
        }
      },
      () => {},
      "GET"
    );
  }, []);
  const isAuthPage = currLocation.pathname !== "/login" && currLocation.pathname !== "/license";
  return (
    <Layout style={{ minHeight: "100vh" }}>
      {isAuthPage && (
        <Suspense fallback={<KnightsLoading />}>
          <Header dateDiffer={dateDiffer} onMenuToggle={toggleMobileMenu} />
        </Suspense>
      )}
      {isAuthPage && (
        <Drawer
          title={null}
          placement="left"
          onClose={closeMobileMenu}
          visible={mobileMenuOpen}
          width={300}
          closable={false}
          bodyStyle={{ padding: "12px 0" }}
        >
          <MobileMenu onClose={closeMobileMenu} />
        </Drawer>
      )}

      <KnightsLoading />
      <ReactTooltip
        id="knights_tooltip"
        effect="float"
        className="knights-tooltip"
        backgroundColor={"#3d3d3d"}
      />

      <Layout.Content>
      <Suspense fallback={<KnightsLoading />}>
        <Routes>
          <Route exact path="/">
            <Route index element={<Navigate to="products" replace />} />
            <Route exact path="license" element={<KnightsLicense />} />
            <Route exact path="charges" element={<KnightsCharges />} />
            <Route exact path="products" element={<Products />} />
            <Route exact path="products/integrated" element={<IntegratedProducts />} />
            <Route exact path="invoice">
              <Route exact path="add_invoice" element={<KnightsNewInvoice />} />
              <Route exact path="view_invoices" element={<KnightsReviewInvoice />} />
              <Route exact path="view_invoices/:id" element={<ReturnInvoiceProduct />} />
              <Route exact path="view_fund" element={<KnightsReviewFund />} />
              <Route element={<KnightsOnlineOrder />} exact path="online_order" />
              <Route element={<WebsiteOrders />} exact path="website_order" />
            </Route>
            <Route exact path="fund">
              <Route exact path="open_fund" element={<KnightsOpenFundReview />} />
            </Route>
            <Route exact path="admin">
              <Route exact path="employee" element={<KnightsEmployees />} />
              <Route exact path="department">
                <Route exact path="new_order" element={<KnightsDepartmentOrder />} />
                <Route exact path="view_order" element={<KnightsViewDepartmentsOrders />} />
                <Route exact path="view_debt" element={<KnightsDepartmentDebt />} />
              </Route>
              <Route exact path="view_department" element={<KnightsDepartments />} />
              <Route exact path="settings">
                <Route exact path="position" element={<KnightsEmployeePosition />} />
                <Route exact path="permission" element={<KnightsPermission />} />
                <Route exact path="permission_category" element={<KnightsPermissionCategory />} />
                <Route exact path="roles" element={<KnightsRole />}></Route>
                <Route exact path="admin_settings" element={<KnightsAdminSetting />} />
                <Route exact path="invoice_template" element={<InvoiceTemplateSettings />} />
                <Route exact path="print_setting" element={<KnightsPrintSetting />} />
                <Route exact path="box" element={<KnightsBox />} />
                <Route exact path="box/transfer" element={<KnightsVaultTransfer />} />
                <Route exact path="box/transfer/history" element={<KnightsVaultTransferReview />} />
                <Route exact path="audit_logs" element={<AuditLogs />} />
              </Route>
            </Route>
            <Route exact path="customer">
              <Route exact path="view" element={<KnightsCustomer />} />
              <Route exact path="debt" element={<KnightsCustomerDebt />} />
              <Route exact path="whatsapp" element={<KnightsWhatsApp />} />
              <Route exact path="whatsapp_queue" element={<KnightsWhatsAppQueue />} />
            </Route>
            <Route exact path="supplier">
              <Route exact path="view" element={<KnightsSupplier />} />
              <Route exact path="invoice" element={<KnightsSupplierInvoice />} />
              <Route exact path="debt" element={<KnightsSupplierDebt />} />
              <Route exact path="view_invoice" element={<KnightsSupplierReviewInvoice />} />
            </Route>
            <Route exact path="category" element={<KnghitsCategory />} />
            <Route exact path="service" element={<KnightsService />} />
            <Route exact path="setting" element={<KnightsAppSetting />} />
            <Route exact path="login" element={<Login />} />
            <Route exact path="print_barcode" element={<KnightsPrintBarcode />} />
            <Route exact path="barcode_designer" element={<BarcodeDesigner />} />
            <Route exact path="inventory">
              <Route exact path="summery" element={<KnightsDefaultInventory />} />
              <Route exact path="summeryProduct" element={<KnightsDefaultInventoryProduct />} />
              <Route exact path="product" element={<KnightsInventoryProduct />} />
              <Route exact path="summeryEmp" element={<KnightsDefaultInventoryEmp />} />
              <Route exact path="stat" element={<KnightsInventoryStat />} />
            </Route>
            <Route exact path="whatsapp">
              <Route exact path="whatsapp_product" element={<KnightsWhatsApp />} />
              <Route exact path="whatsapp_queue" element={<KnightsWhatsAppQueue />} />
              <Route exact path="whatsapp_settings" element={<DebtReminderSettings />} />
            </Route>
            <Route exact path="reports">
              <Route exact path="daily" element={<ReportsPage />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
      </Layout.Content>
    </Layout>
  );
}
export default App;
